<template>
  <div class="basket">
    <div class="page container px-3 mt-3">
      <h1 class="custom-title">Your order is confirmed</h1>

      <Divider />
      <h5 class="text-center">Order #: {{ order.orderId }}</h5>
      <Divider />

      <div
        v-for="(product, index) in products"
        :key="product.id + index"
        class="basket__product mb-3 mt-4"
      >
        <div class="basket__product-preview mr-3">
          <img
            v-if="isDigitalFormat(product)"
            class="basket__product-preview-digital-badge"
            src="@/assets/icons/pdf-badge.svg"
            alt="digital-format"
          />
          <img
            class="basket__product-preview-image"
            :src="getPreviewImage(product)"
            :alt="`preview-${product.id}`"
          />
        </div>
        <div class="basket__product-details">
          <h4>{{ product.name }}</h4>
          <p v-if="product.creator" class="mb-0">
            @{{ $get(product, 'creator.profile.handle') }}
          </p>
          <p class="basket__product-details-availability mb-0">
            {{ product.availability }}
          </p>
          <div class="basket__product-quantity">
            <span class="mr-2">Quantity: {{ product.quantity }}</span>
          </div>
        </div>
      </div>

      <Divider />

      <Button
        class="mb-5 mt-5"
        full-width
        variant="outline"
        @click="handleBackToStore"
      >
        Back to store
      </Button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex';
import { find, map } from 'lodash';
import { fetchOrderBySid } from '../../../services/orders';
import { fetchProducts } from '@/services/products';

import Button from '~/components/Elements/Button';
import Divider from '~/components/Elements/Divider';

import {
  FallbackImageURL,
  ProductFormatFulfilment,
  ProductFormatAvailability
} from '@/misc/constants';
import formatMoney from '@/misc/formatMoney';
import { trackPurchaseEvent, trackSaleEvent } from '@/lib/analytics';
import getFormatPrice from '@/misc/getFormatPrice';

export default {
  name: 'SuccessPage',

  components: {
    Button,
    Divider
  },

  data() {
    return {
      order: {},
      products: [],
      discount: null
    };
  },

  head: {
    title: 'Success order | Found'
  },

  computed: {
    ...mapState('main', ['payoutCode']),
    ...mapGetters({
      currency: 'config/currency',
      region: 'config/region'
    }),
    subtotalPrice() {
      return this.products?.reduce((sum, item) => {
        const { amount } = getFormatPrice(item.productFormat, this.region);
        return sum + item.quantity * amount;
      }, 0);
    },
    shippingPrice() {
      return this.$get(this.order, 'shippingPrice', 0);
    },
    discountPrice() {
      return (this.subtotalPrice * this.discount?.percentOff) / 100;
    },
    totalPrice() {
      let total = !this.isAllItemsHaveDigitalFormat
        ? this.shippingPrice + this.subtotalPrice
        : this.subtotalPrice;
      if (this.discount) {
        total = total - this.discountPrice;
      }
      return total;
    },
    formattedShippingPrice() {
      return this.shippingPrice === 0
        ? 'FREE'
        : formatMoney(this.shippingPrice, this.region);
    },
    isAllItemsHaveDigitalFormat() {
      return this.products?.every(
        ({ productFormat }) =>
          productFormat.fulfilment === ProductFormatFulfilment.Digital
      );
    }
  },

  mounted() {
    trackSaleEvent('Thank you page');
    if (this.payoutCode) {
      this.REMOVE_PAYOUT_CODE();
    }
    const sessionId = this.$route.query?.session_id;
    fetchOrderBySid(this.$axios, sessionId).then((orderInfo) => {
      this.order = orderInfo;
      this.discount = orderInfo.discount;
      const lineItems = this.$get(orderInfo, 'lineItems');
      const productIds = map(lineItems, 'item.product._id');
      fetchProducts(this.$axios, {
        query: { $where: { _id: { $in: productIds } }, $limit: 50 }
      }).then((response) => {
        const hpStoreProducts = this.$get(response, 'data', []);
        this.products = map(lineItems, (lineItem) => {
          const productId = this.$get(lineItem, 'item.product._id');
          const quantity = this.$get(lineItem, 'item.quantity');
          const itemFormatId = this.$get(lineItem, 'item.productFormatId');
          const product = find(hpStoreProducts, ['_id', productId]);
          const productFormat = find(product.formats, ['_id', itemFormatId]);
          const isGarmentsProduct =
            product.formats?.filter(
              ({ availability, deletedAt }) =>
                !deletedAt &&
                availability === ProductFormatAvailability.Available
            ).length > 4;

          return {
            ...product,
            quantity,
            productFormat,
            productFormatId: itemFormatId,
            isGarmentsProduct
          };
        });

        trackPurchaseEvent({ order: this.order, cart: this.products });
        this.CLEAR_ITEMS();
      });
    });
  },

  methods: {
    ...mapMutations({
      REMOVE_PAYOUT_CODE: 'main/REMOVE_PAYOUT_CODE',
      CLEAR_ITEMS: 'checkout/CLEAR_ITEMS'
    }),
    formatMoney,
    getPreviewImage(product) {
      if (product?.previewImages && product?.previewImages?.length) {
        if (product.isGarmentsProduct) {
          // extract design name ("Design 1" from "Design 1 - L")
          const productFormatName = this.getProductFormatName(product);
          const [designName] = productFormatName.split(' - ');

          if (!designName) return null;

          // find matching image by meta
          const image = product.previewImages.find(
            (img) => img.options?.design === designName
          );

          if (image) {
            return image.url;
          }
        }

        return product?.previewImages[0]?.url;
      }
      return FallbackImageURL;
    },
    handleBackToStore() {
      this.$router.push({ path: '/' });
    },
    getProductFormatPrice(product) {
      const { productFormat } = product;
      return formatMoney(
        getFormatPrice(productFormat, this.region)?.amount,
        this.currency
      );
    },
    getProductFormatName(product) {
      const { productFormat } = product;
      return this.$get(productFormat, 'name');
    },
    isDigitalFormat(product) {
      return (
        product?.productFormat?.fulfilment === ProductFormatFulfilment.Digital
      );
    }
  }
};
</script>

<style lang="scss" scoped>
.basket {
  &__product {
    display: flex;

    &-preview {
      position: relative;

      &-image {
        width: 110px;
        height: 110px;
        border-radius: 5px;
      }

      &-digital-badge {
        position: absolute;
        width: 30px;
        right: 5px;
        top: 0;
      }
    }

    &-details {
      &-price {
        font-weight: 700;
        color: $primary-color;
      }

      &-availability {
        font-size: 12px;
      }
    }
  }

  &__subtotal,
  &__shipping,
  &__total,
  &__discount {
    margin-left: 125px;

    .price {
      font-weight: 700;
      color: $primary-color;
    }
  }
}
</style>
